import React, { FC, PropsWithChildren, ReactElement, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Sticky, StickyContainer } from 'react-sticky';
import { MessageOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import classNames from 'classnames';
import {
  DIRECT_MESSAGE_AREA_PATTERNS,
  DropDownSelectorValues,
  ProjectStatus,
} from 'providence-types';
import { ActionRes, OptionProps, SiteOptions } from '@models/interfaces';
import { ProjectCategoriesActions } from '@store/actions';
import {
  CommonSelectors,
  DDVSelectors,
  ProjectSelectors,
} from '@store/selectors';
import { HeaderBarProps } from './models/interfaces';
import { InfoBox, Item } from './components';
import { DirectMessageManager } from './modals';

import './styles.scss';

export const HeaderBar: FC<HeaderBarProps> = ({
  className,
  category = false,
  site,
  siteOptions = [
    {
      key: 'FALocationNumber',
      label: 'FA Location Number',
    },
    {
      key: 'siteNumber',
      label: 'Site Number',
    },
  ],
  showTowerOwner = true,
  withoutTabPanel = false,
}: PropsWithChildren<HeaderBarProps>) => {
  const dispatch = useDispatch();

  const { currentProjectCategories, projectCategoriesTableVisible } =
    useSelector(ProjectSelectors.getProjectCategories);
  const holdOptions = useSelector(ProjectSelectors.getHoldOptions);
  const providenceAgent = useSelector(ProjectSelectors.getProvidenceAgent);
  const towerOwners = useSelector(
    DDVSelectors.getDDVValuesByType(DropDownSelectorValues.TowerOwner),
  ) as OptionProps[];
  const riskCategories = useSelector(
    DDVSelectors.getDDVValuesByType(DropDownSelectorValues.RiskCategory),
  ) as OptionProps[];
  const routePath = useSelector(CommonSelectors.getRoutePath);

  const [visibleDMManager, setVisibleDMManager] = useState<boolean>(false);

  return (
    <StickyContainer>
      <Sticky>
        {(): ReactElement => (
          <div className={classNames('prov-header-bar', className)}>
            <div className="prov-header-bar__left">
              {siteOptions.map(
                ({ label, key }: SiteOptions) =>
                  site && <Item key={key} label={label} value={site[key]} />,
              )}
              {showTowerOwner && (
                <Item
                  key="towerOwner"
                  label="Tower Owner"
                  value={
                    towerOwners.find(
                      ({ value }: OptionProps) =>
                        (site?.towerOwner as unknown as string) === value,
                    )?.viewValue
                  }
                />
              )}
              {category && (
                <>
                  <Item
                    key="projectCategory"
                    label="Project Category"
                    element={
                      <span
                        className={classNames(
                          'item__element project-category',
                          {
                            'project-category--active':
                              !projectCategoriesTableVisible,
                          },
                        )}
                        onClick={(): ActionRes<void> =>
                          dispatch(
                            ProjectCategoriesActions.toggleProjectCategoriesTableAction.done(),
                          )
                        }
                      >
                        {projectCategoriesTableVisible
                          ? 'X'
                          : currentProjectCategories?.length}
                      </span>
                    }
                  />
                  <Item
                    key="providenceAgent"
                    label="Providence Agent"
                    value={providenceAgent}
                  />
                </>
              )}
            </div>
            <div
              className={classNames('prov-header-bar__right', {
                margin: withoutTabPanel,
              })}
            >
              {site?.siteOn === false ? (
                <InfoBox
                  status="inactive"
                  title="Off"
                  value={site?.disablingNotes}
                />
              ) : (
                site?.highRiskSite && (
                  <InfoBox
                    status="default"
                    title="High Risk Site"
                    value={
                      riskCategories.find(
                        (riskCategory: OptionProps) =>
                          riskCategory.value ===
                          (site?.riskCategory as unknown as string),
                      )?.viewValue
                    }
                  />
                )
              )}
              {holdOptions?.projectStatus === ProjectStatus.Hold && (
                <InfoBox
                  status="hold"
                  title="Hold"
                  value={holdOptions?.holdReason}
                />
              )}
              {DIRECT_MESSAGE_AREA_PATTERNS.has(routePath) && (
                <>
                  <Button
                    className="direct-message-btn"
                    onClick={(): void => setVisibleDMManager(true)}
                  >
                    <MessageOutlined />
                  </Button>
                  <DirectMessageManager
                    visible={visibleDMManager}
                    areaKey={DIRECT_MESSAGE_AREA_PATTERNS.get(routePath)!}
                    onClose={(): void => setVisibleDMManager(false)}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </Sticky>
    </StickyContainer>
  );
};
